import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import { useAuth } from "@/app/auth-context-state";

import cls from "./CreateMemberRealImage.module.scss";

/* Placeholders (q2) */
import mrImg from "@/assets/UI/memberCreate/q2Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q2MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q2Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q2MrsM.webp";

/* processed/{username}/{uuid}/images/poster.webp */
const DEFAULT_PROCESSED_SUBPATH = "images/poster.webp";

/* Framer: same “Welcome” gentle floating (match Avatar step) */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Same helper pattern you use in other member-create pages */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Map layout context to persona bucket (kept from your original file) */
function getPersona(memberType, rawGender) {
  if (memberType === "couple") return "couple";
  if (rawGender === "man" || rawGender === "trans_man") return "man";
  if (rawGender === "woman" || rawGender === "trans_woman") return "woman";
  return "nb";
}

export default function CreateMemberRealImage() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;

  const isCouple = isCoupleCtx || memberType === "couple";
  const persona = getPersona(memberType, rawGender);

  const memberInputRef = useRef(null);
  const partnerInputRef = useRef(null);

  // MEMBER
  const [fileMember, setFileMember] = useState(null);
  const [previewMember, setPreviewMember] = useState("");
  const [uploadingMember, setUploadingMember] = useState(false);
  const [progressMember, setProgressMember] = useState(0);
  const [processedBaseKeyMember, setProcessedBaseKeyMember] = useState("");

  // PARTNER (couple only)
  const [filePartner, setFilePartner] = useState(null);
  const [previewPartner, setPreviewPartner] = useState("");
  const [uploadingPartner, setUploadingPartner] = useState(false);
  const [progressPartner, setProgressPartner] = useState(0);
  const [processedBaseKeyPartner, setProcessedBaseKeyPartner] = useState("");

  const [finalizing, setFinalizing] = useState(false);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  /* -------- i18n copy (kept compatible with your existing keys) -------- */

  const title = i18n.t("onboarding.real_image.title", {
    defaultValue: "Real photo for verification",
  });

  const subtitle = (() => {
    const key = `onboarding.real_image.${persona}.intro`;
    const fallback =
      persona === "couple"
        ? "Upload a clear natural photo for you and your partner. This stays private and is only used for verification."
        : "Upload a clear natural photo of your face. This stays private and is only used for verification.";
    return i18n.t(key, { defaultValue: fallback });
  })();

  const note = (() => {
    const key = `onboarding.real_image.${persona}.note`;
    return i18n.t(key, {
      defaultValue:
        "Tip: no sunglasses, no heavy filters. Good lighting makes verification faster.",
    });
  })();

  const afterUpload = (() => {
    const key = `onboarding.real_image.${persona}.after`;
    return i18n.t(key, {
      defaultValue: "Saved! Verification photo received.",
    });
  })();

  const ctaUpload = i18n.t("common.upload", { defaultValue: "Upload" });
  const ctaSave = i18n.t("common.saveContinue", {
    defaultValue: "Save & Continue",
  });

  const labelMember = i18n.t("onboarding.real_image.memberLabel", {
    defaultValue: "Your verification photo",
  });

  const labelPartner = i18n.t("onboarding.real_image.partnerLabel", {
    defaultValue: "Partner’s verification photo",
  });

  /* --------------------------- helpers --------------------------- */

  function cleanupPreview(url) {
    try {
      if (url) URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      cleanupPreview(previewMember);
      cleanupPreview(previewPartner);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickMemberFile(f) {
    if (!f) return;

    if (!/^image\//.test(f.type)) {
      toast.info(
        i18n.t("onboarding.real_image.onlyImages", {
          defaultValue: "Please choose an image file.",
        }),
      );
      return;
    }

    setFileMember(f);
    cleanupPreview(previewMember);
    setPreviewMember(URL.createObjectURL(f));

    // reset upload state
    setProcessedBaseKeyMember("");
    setProgressMember(0);
  }

  function pickPartnerFile(f) {
    if (!f) return;

    if (!/^image\//.test(f.type)) {
      toast.info(
        i18n.t("onboarding.real_image.onlyImages", {
          defaultValue: "Please choose an image file.",
        }),
      );
      return;
    }

    setFilePartner(f);
    cleanupPreview(previewPartner);
    setPreviewPartner(URL.createObjectURL(f));

    // reset upload state
    setProcessedBaseKeyPartner("");
    setProgressPartner(0);
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  function onMemberPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    pickMemberFile(f);
  }

  function onPartnerPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    pickPartnerFile(f);
  }

  function onMemberDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    pickMemberFile(f);
  }

  function onPartnerDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    pickPartnerFile(f);
  }

  async function uploadOriginal({
    file,
    setUploading,
    setProgress,
    setBaseKey,
  }) {
    if (!file) {
      toast.info(
        i18n.t("onboarding.real_image.pickFirst", {
          defaultValue: "Choose an image first.",
        }),
      );
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";

      const { data } = await api.post("/v1/member-create/real-image/presign", {
        ext,
        contentType: file.type || "application/octet-stream",
      });

      const { uploadUrl, processedBaseKey } = data || {};
      if (!uploadUrl || !processedBaseKey)
        throw new Error("Invalid presign response");

      setBaseKey(processedBaseKey);

      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });

      setProgress(100);
      toast.success(
        i18n.t("onboarding.real_image.uploaded", {
          defaultValue: "Uploaded! Processing…",
        }),
      );
    } catch (err) {
      console.error("real-image upload failed:", err);
      toast.error(
        i18n.t("onboarding.real_image.uploadError", {
          defaultValue: "Upload failed. Please try again.",
        }),
      );
      setBaseKey("");
      setProgress(0);
    } finally {
      setUploading(false);
    }
  }

  async function onFinalize(e) {
    e.preventDefault();

    if (!processedBaseKeyMember) {
      toast.info(
        i18n.t("onboarding.real_image.needMemberFirst", {
          defaultValue: "Upload your verification photo first.",
        }),
      );
      return;
    }

    if (isCouple && !processedBaseKeyPartner) {
      toast.info(
        i18n.t("onboarding.real_image.needPartnerFirst", {
          defaultValue: "Upload your partner’s verification photo too.",
        }),
      );
      return;
    }

    setFinalizing(true);
    try {
      const processedKeyMember = `${processedBaseKeyMember}/${DEFAULT_PROCESSED_SUBPATH}`;

      const payload = {
        subject: isCouple ? "couple" : "single",
        processedKeyMember,
      };

      if (isCouple) {
        payload.processedKeyPartner = `${processedBaseKeyPartner}/${DEFAULT_PROCESSED_SUBPATH}`;
      }

      const { data } = await api.post(
        "/v1/member-create/real-image/finalize",
        payload,
      );

      await refreshMe();
      toast.success(afterUpload);

      navigate(data?.next || "/member/social", { replace: true });
    } catch (err) {
      console.error("finalize real-image failed:", err);
      toast.error(
        i18n.t("onboarding.real_image.finalizeError", {
          defaultValue:
            "Couldn’t save your verification photo. If processing is still running, try again in a moment.",
        }),
      );
    } finally {
      setFinalizing(false);
    }
  }

  const hasUploadedMember = Boolean(processedBaseKeyMember);
  const hasUploadedPartner = !isCouple || Boolean(processedBaseKeyPartner);
  const submitDisabled =
    finalizing || !hasUploadedMember || !hasUploadedPartner;

  return (
    <main className={cls.page}>
      {/* Fixed background stage (Avatar-style) */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Content layer */}
      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <div className={cls.formTop}>
              <div className={cls.formMeta}>
                <div className={cls.formTitle}>{title}</div>
                <div className={cls.formSub}>{subtitle}</div>
              </div>
            </div>

            <form className={cls.form} onSubmit={onFinalize} noValidate>
              <div
                className={`${cls.uploadsGrid} ${isCouple ? cls.twoCols : ""}`}
              >
                {/* MEMBER uploader */}
                <div className={cls.uploaderBlock}>
                  <div className={cls.fieldLabel}>{labelMember}</div>

                  <div
                    className={`${cls.dropzone} ${
                      fileMember ? cls.hasFile : ""
                    }`}
                    onDrop={onMemberDrop}
                    onDragOver={onDragOver}
                    role="button"
                    tabIndex={0}
                    onClick={() => memberInputRef.current?.click()}
                    aria-label={labelMember}
                  >
                    {previewMember ? (
                      <div className={cls.previewWrap}>
                        <img
                          src={previewMember}
                          alt=""
                          className={cls.preview}
                        />
                        <div className={cls.previewShade} />
                        <div className={cls.previewMeta}>
                          <div className={cls.previewTitle}>
                            {i18n.t("onboarding.real_image.selected", {
                              defaultValue: "Selected",
                            })}
                          </div>
                          <div className={cls.previewSub}>
                            {i18n.t("onboarding.real_image.tapChange", {
                              defaultValue: "Tap to change",
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={cls.dropInner}>
                        <span className={cls.dropEmoji} aria-hidden="true">
                          📸
                        </span>
                        <strong>
                          {i18n.t("onboarding.real_image.chooseMember", {
                            defaultValue: "Choose your photo",
                          })}
                        </strong>
                        <small>
                          {i18n.t("onboarding.real_image.hint", {
                            defaultValue:
                              "PNG/JPG/WebP • Face visible • No heavy filters",
                          })}
                        </small>
                      </div>
                    )}

                    <input
                      ref={memberInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onMemberPick}
                      className="sr-only"
                    />
                  </div>

                  <div className={cls.actionsRow}>
                    <button
                      type="button"
                      className={cls.btn}
                      onClick={() =>
                        uploadOriginal({
                          file: fileMember,
                          setUploading: setUploadingMember,
                          setProgress: setProgressMember,
                          setBaseKey: setProcessedBaseKeyMember,
                        })
                      }
                      disabled={!fileMember || uploadingMember}
                      aria-busy={uploadingMember ? "true" : "false"}
                    >
                      {uploadingMember
                        ? i18n.t("common.uploading", {
                            defaultValue: "Uploading…",
                          })
                        : ctaUpload}
                    </button>

                    <div className={cls.progressWrap} aria-hidden={false}>
                      <div className={cls.progressTrack}>
                        <div
                          className={cls.progressBar}
                          style={{ width: `${progressMember}%` }}
                        />
                      </div>
                      <div className={cls.progressText}>
                        {uploadingMember
                          ? `${progressMember}%`
                          : hasUploadedMember
                            ? "100%"
                            : progressMember
                              ? `${progressMember}%`
                              : "0%"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* PARTNER uploader */}
                {isCouple && (
                  <div className={cls.uploaderBlock}>
                    <div className={cls.fieldLabel}>{labelPartner}</div>

                    <div
                      className={`${cls.dropzone} ${
                        filePartner ? cls.hasFile : ""
                      }`}
                      onDrop={onPartnerDrop}
                      onDragOver={onDragOver}
                      role="button"
                      tabIndex={0}
                      onClick={() => partnerInputRef.current?.click()}
                      aria-label={labelPartner}
                    >
                      {previewPartner ? (
                        <div className={cls.previewWrap}>
                          <img
                            src={previewPartner}
                            alt=""
                            className={cls.preview}
                          />
                          <div className={cls.previewShade} />
                          <div className={cls.previewMeta}>
                            <div className={cls.previewTitle}>
                              {i18n.t("onboarding.real_image.selected", {
                                defaultValue: "Selected",
                              })}
                            </div>
                            <div className={cls.previewSub}>
                              {i18n.t("onboarding.real_image.tapChange", {
                                defaultValue: "Tap to change",
                              })}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={cls.dropInner}>
                          <span className={cls.dropEmoji} aria-hidden="true">
                            📸
                          </span>
                          <strong>
                            {i18n.t("onboarding.real_image.choosePartner", {
                              defaultValue: "Choose partner photo",
                            })}
                          </strong>
                          <small>
                            {i18n.t("onboarding.real_image.hintPartner", {
                              defaultValue:
                                "Face visible • Natural is best • No heavy filters",
                            })}
                          </small>
                        </div>
                      )}

                      <input
                        ref={partnerInputRef}
                        type="file"
                        accept="image/*"
                        onChange={onPartnerPick}
                        className="sr-only"
                      />
                    </div>

                    <div className={cls.actionsRow}>
                      <button
                        type="button"
                        className={cls.btn}
                        onClick={() =>
                          uploadOriginal({
                            file: filePartner,
                            setUploading: setUploadingPartner,
                            setProgress: setProgressPartner,
                            setBaseKey: setProcessedBaseKeyPartner,
                          })
                        }
                        disabled={!filePartner || uploadingPartner}
                        aria-busy={uploadingPartner ? "true" : "false"}
                      >
                        {uploadingPartner
                          ? i18n.t("common.uploading", {
                              defaultValue: "Uploading…",
                            })
                          : ctaUpload}
                      </button>

                      <div className={cls.progressWrap} aria-hidden={false}>
                        <div className={cls.progressTrack}>
                          <div
                            className={cls.progressBar}
                            style={{ width: `${progressPartner}%` }}
                          />
                        </div>
                        <div className={cls.progressText}>
                          {uploadingPartner
                            ? `${progressPartner}%`
                            : hasUploadedPartner
                              ? "100%"
                              : progressPartner
                                ? `${progressPartner}%`
                                : "0%"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sticky submit (Avatar-style) */}
              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submit}
                  disabled={submitDisabled}
                  aria-busy={finalizing ? "true" : "false"}
                >
                  {finalizing
                    ? i18n.t("common.saving", { defaultValue: "Saving…" })
                    : ctaSave}
                </button>
              </div>

              {/* Helpful note */}
              <div className={cls.helpNote}>
                <span className={cls.helpDot} aria-hidden="true" />
                {note}
              </div>
            </form>
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
